import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Typography,
	Paper,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Button,
	ButtonGroup,
	Chip,
	Alert,
	Divider,
	TextField,
	InputAdornment,
	Pagination,
	CircularProgress,
	IconButton,
} from "@mui/material";
import {
	Bell,
	Mail,
	MailOpen,
	Clock,
	Search,
	MailCheck,
	MailX,
	Trash2,
	Video,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useAcademicYear } from "../../contexts/AcademicYearContext";
import { ROLES } from "../../config/supabase";
import { applyCurrentAcademicYearNotificationFilter } from "../../utils/notifications";
import {
	GAME_RESULTS_UPLOAD_PATH,
	isEditedVideoNotification,
} from "../../utils/gameResultNotifications";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const MyNotifications = () => {
	const { user, supabase } = useAuth();
	const navigate = useNavigate();
	const { currentAcademicYear, loading: academicYearLoading } = useAcademicYear();
	const isTeacher = user?.profile?.role === ROLES.SCHOOL_TEACHER;
	const [notifications, setNotifications] = useState([]);
	const [filteredNotifications, setFilteredNotifications] = useState([]);
	const [loading, setLoading] = useState(true);
	const [filter, setFilter] = useState("all"); // "all", "unread", "read"
	const [searchTerm, setSearchTerm] = useState("");
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [stats, setStats] = useState({ total: 0, unread: 0, read: 0 });
	const itemsPerPage = 10;

	useEffect(() => {
		if (isTeacher && academicYearLoading) return;
		fetchNotifications();
	}, [user.id, page, currentAcademicYear, isTeacher, academicYearLoading]);

	useEffect(() => {
		applyFilters();
	}, [notifications, filter, searchTerm]);

	const withAcademicYearFilter = (query) => {
		if (!isTeacher) return query;
		return applyCurrentAcademicYearNotificationFilter(query, currentAcademicYear);
	};

	const fetchNotifications = async () => {
		try {
			setLoading(true);
			if (isTeacher && !currentAcademicYear) {
				setNotifications([]);
				setFilteredNotifications([]);
				setStats({ total: 0, unread: 0, read: 0 });
				setTotalPages(1);
				return;
			}
			const offset = (page - 1) * itemsPerPage;

			let listQuery = supabase
				.from("notifications")
				.select(
					`
					*,
					sender:profiles!sender_id(full_name, email)
				`,
					{ count: "exact" }
				)
				.eq("recipient_id", user.id)
				.order("created_at", { ascending: false })
				.range(offset, offset + itemsPerPage - 1);

			listQuery = withAcademicYearFilter(listQuery);

			const { data, error, count } = await listQuery;

			if (error) throw error;

			setNotifications(data || []);
			setTotalPages(Math.ceil((count || 0) / itemsPerPage));

			let statsQuery = supabase
				.from("notifications")
				.select("is_read")
				.eq("recipient_id", user.id);

			statsQuery = withAcademicYearFilter(statsQuery);

			const { data: allNotifications, error: statsError } = await statsQuery;

			if (!statsError && allNotifications) {
				const total = allNotifications.length;
				const unread = allNotifications.filter((n) => !n.is_read).length;
				const read = total - unread;
				setStats({ total, unread, read });
			}
		} catch (err) {
			console.error("Error fetching notifications:", err);
		} finally {
			setLoading(false);
		}
	};

	const applyFilters = () => {
		let filtered = [...notifications];

		// Apply read/unread filter
		if (filter === "unread") {
			filtered = filtered.filter((n) => !n.is_read);
		} else if (filter === "read") {
			filtered = filtered.filter((n) => n.is_read);
		}

		// Apply search filter
		if (searchTerm) {
			const search = searchTerm.toLowerCase();
			filtered = filtered.filter(
				(n) =>
					n.subject.toLowerCase().includes(search) ||
					n.message.toLowerCase().includes(search) ||
					n.sender?.full_name.toLowerCase().includes(search)
			);
		}

		setFilteredNotifications(filtered);
	};

	const markAsRead = async (notificationId) => {
		try {
			const { error } = await supabase
				.from("notifications")
				.update({ is_read: true, read_at: new Date().toISOString() })
				.eq("id", notificationId);

			if (error) throw error;

			// Update local state
			setNotifications((prev) =>
				prev.map((n) =>
					n.id === notificationId
						? { ...n, is_read: true, read_at: new Date().toISOString() }
						: n
				)
			);

			// Update stats
			setStats((prev) => ({
				...prev,
				unread: Math.max(0, prev.unread - 1),
				read: prev.read + 1,
			}));
		} catch (err) {
			console.error("Error marking notification as read:", err);
		}
	};

	const markAsUnread = async (notificationId) => {
		try {
			const { error } = await supabase
				.from("notifications")
				.update({ is_read: false, read_at: null })
				.eq("id", notificationId);

			if (error) throw error;

			// Update local state
			setNotifications((prev) =>
				prev.map((n) =>
					n.id === notificationId ? { ...n, is_read: false, read_at: null } : n
				)
			);

			// Update stats
			setStats((prev) => ({
				...prev,
				unread: prev.unread + 1,
				read: Math.max(0, prev.read - 1),
			}));
		} catch (err) {
			console.error("Error marking notification as unread:", err);
		}
	};

	const deleteNotification = async (notificationId) => {
		try {
			const { error } = await supabase
				.from("notifications")
				.delete()
				.eq("id", notificationId);

			if (error) throw error;

			// Update local state
			const notificationToDelete = notifications.find(
				(n) => n.id === notificationId
			);
			setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

			// Update stats
			if (notificationToDelete) {
				setStats((prev) => ({
					total: prev.total - 1,
					unread: notificationToDelete.is_read
						? prev.unread
						: Math.max(0, prev.unread - 1),
					read: notificationToDelete.is_read
						? Math.max(0, prev.read - 1)
						: prev.read,
				}));
			}
		} catch (err) {
			console.error("Error deleting notification:", err);
		}
	};

	const markAllAsRead = async () => {
		try {
			const unreadNotifications = notifications.filter((n) => !n.is_read);
			const unreadIds = unreadNotifications.map((n) => n.id);

			if (unreadIds.length === 0) return;

			const { error } = await supabase
				.from("notifications")
				.update({ is_read: true, read_at: new Date().toISOString() })
				.in("id", unreadIds);

			if (error) throw error;

			// Update local state
			setNotifications((prev) =>
				prev.map((n) => ({
					...n,
					is_read: true,
					read_at: new Date().toISOString(),
				}))
			);

			// Update stats
			setStats((prev) => ({
				...prev,
				unread: 0,
				read: prev.total,
			}));
		} catch (err) {
			console.error("Error marking all notifications as read:", err);
		}
	};

	const getNotificationIcon = (notification) => {
		const iconProps = {
			size: 20,
			color: notification.is_read ? "#6b7280" : "#3b82f6",
		};
		if (isEditedVideoNotification(notification)) {
			return <Video {...iconProps} />;
		}
		return notification.is_read ? (
			<MailOpen {...iconProps} />
		) : (
			<Mail {...iconProps} />
		);
	};

	const openEditedVideos = async (notification) => {
		if (!notification.is_read) {
			await markAsRead(notification.id);
		}
		navigate(GAME_RESULTS_UPLOAD_PATH);
	};

	const formatDate = (date) => {
		return dayjs(date).fromNow();
	};

	const handlePageChange = (event, newPage) => {
		setPage(newPage);
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3 }}>
			<Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
				My Notifications
			</Typography>

			{isTeacher && (
				<Alert severity="info" sx={{ mb: 3 }}>
					Showing notifications for academic year {currentAcademicYear} only.
				</Alert>
			)}

			{/* Stats Cards */}
			<Box sx={{ display: "flex", gap: 2, mb: 3 }}>
				<Paper
					elevation={0}
					sx={{
						p: 2,
						border: "1px solid #e5e7eb",
						borderRadius: "8px",
						flex: 1,
					}}>
					<Typography variant="h6" color="primary">
						{stats.total}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Total
					</Typography>
				</Paper>
				<Paper
					elevation={0}
					sx={{
						p: 2,
						border: "1px solid #e5e7eb",
						borderRadius: "8px",
						flex: 1,
					}}>
					<Typography variant="h6" color="error">
						{stats.unread}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Unread
					</Typography>
				</Paper>
				<Paper
					elevation={0}
					sx={{
						p: 2,
						border: "1px solid #e5e7eb",
						borderRadius: "8px",
						flex: 1,
					}}>
					<Typography variant="h6" color="success.main">
						{stats.read}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Read
					</Typography>
				</Paper>
			</Box>

			{/* Filters and Search */}
			<Paper
				elevation={0}
				sx={{ p: 2, mb: 3, border: "1px solid #e5e7eb", borderRadius: "8px" }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						flexWrap: "wrap",
						gap: 2,
					}}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<ButtonGroup variant="outlined" size="small">
							<Button
								variant={filter === "all" ? "contained" : "outlined"}
								onClick={() => setFilter("all")}>
								All ({stats.total})
							</Button>
							<Button
								variant={filter === "unread" ? "contained" : "outlined"}
								onClick={() => setFilter("unread")}>
								Unread ({stats.unread})
							</Button>
							<Button
								variant={filter === "read" ? "contained" : "outlined"}
								onClick={() => setFilter("read")}>
								Read ({stats.read})
							</Button>
						</ButtonGroup>

						{stats.unread > 0 && (
							<Button
								size="small"
								variant="outlined"
								onClick={markAllAsRead}
								startIcon={<MailCheck size={16} />}>
								Mark All Read
							</Button>
						)}
					</Box>

					<TextField
						size="small"
						placeholder="Search notifications..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search size={20} />
								</InputAdornment>
							),
						}}
						sx={{ minWidth: 250 }}
					/>
				</Box>
			</Paper>

			{/* Notifications List */}
			<Paper
				elevation={0}
				sx={{ border: "1px solid #e5e7eb", borderRadius: "8px" }}>
				{filteredNotifications.length === 0 ? (
					<Box sx={{ p: 3, textAlign: "center" }}>
						<Bell size={48} color="#9ca3af" />
						<Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
							{filter === "all"
								? "No notifications found"
								: filter === "unread"
								? "No unread notifications"
								: "No read notifications"}
						</Typography>
						{searchTerm && (
							<Typography variant="body2" color="text.secondary">
								Try adjusting your search terms
							</Typography>
						)}
					</Box>
				) : (
					<List sx={{ p: 0 }}>
						{filteredNotifications.map((notification, index) => (
							<React.Fragment key={notification.id}>
								<ListItem
									sx={{
										py: 2,
										px: 3,
										bgcolor: notification.is_read
											? "transparent"
											: "rgba(59, 130, 246, 0.05)",
										"&:hover": {
											bgcolor: notification.is_read
												? "rgba(0, 0, 0, 0.04)"
												: "rgba(59, 130, 246, 0.1)",
										},
									}}>
									<ListItemIcon sx={{ minWidth: 40 }}>
										{getNotificationIcon(notification)}
									</ListItemIcon>
									<ListItemText
										primary={
											<Box
												sx={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "flex-start",
													mb: 1,
												}}>
												<Typography
													variant="subtitle1"
													sx={{
														fontWeight: notification.is_read
															? "normal"
															: "bold",
														color: notification.is_read
															? "text.secondary"
															: "text.primary",
													}}>
													{notification.subject}
												</Typography>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
													}}>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 0.5,
														}}>
														<Clock size={12} />
														{formatDate(notification.created_at)}
													</Typography>
													{!notification.is_read ? (
														<Chip label="New" size="small" color="primary" />
													) : (
														<Chip
															label="Read"
															size="small"
															variant="outlined"
														/>
													)}
													{isEditedVideoNotification(notification) && (
														<Chip
															label="Edited video"
															size="small"
															color="success"
															variant="outlined"
														/>
													)}
												</Box>
											</Box>
										}
										secondary={
											<Box>
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ mb: 1 }}>
													{notification.message}
												</Typography>
												{notification.sender && (
													<Typography variant="caption" color="text.secondary">
														From: {notification.sender.full_name}
													</Typography>
												)}
												{isEditedVideoNotification(notification) && (
													<Button
														size="small"
														variant="outlined"
														sx={{ mt: 1 }}
														onClick={() => openEditedVideos(notification)}>
														View edited videos
													</Button>
												)}
											</Box>
										}
									/>
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											gap: 1,
											ml: 2,
										}}>
										<IconButton
											size="small"
											onClick={() =>
												notification.is_read
													? markAsUnread(notification.id)
													: markAsRead(notification.id)
											}
											title={
												notification.is_read ? "Mark as unread" : "Mark as read"
											}>
											{notification.is_read ? (
												<MailX size={16} />
											) : (
												<MailCheck size={16} />
											)}
										</IconButton>
										<IconButton
											size="small"
											color="error"
											onClick={() => deleteNotification(notification.id)}
											title="Delete notification">
											<Trash2 size={16} />
										</IconButton>
									</Box>
								</ListItem>
								{index < filteredNotifications.length - 1 && <Divider />}
							</React.Fragment>
						))}
					</List>
				)}

				{/* Pagination */}
				{totalPages > 1 && (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							p: 2,
							borderTop: "1px solid #e5e7eb",
						}}>
						<Pagination
							count={totalPages}
							page={page}
							onChange={handlePageChange}
							color="primary"
						/>
					</Box>
				)}
			</Paper>
		</Box>
	);
};

export default MyNotifications;
